import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { fetchProgramVpaMappings, fetchTenantList, Tenant } from "../../features/outlet/TenantSlice";
import { fetchProgramById, fetchProgramList } from "../../features/program/ProgramSlice";
import { fetchPrincipalList } from "../../features/principal/PrincipalSlice";
import { RootState } from "../../store/store";
import { AlertCircle, ArrowLeft, Search } from "lucide-react";
import EditTenant from "./EditTenant";
import VpaListing from "./VpaListing";
import TenantList from "./TenantListing";
import BulkTenantUpload from "./BulkTenantUpload";
import * as ExcelJS from 'exceljs';
import { Loader2 } from "lucide-react";

/* --------------------------------- Interfaces ------------------------------ */

interface FileUploadInterface {
  file?: File | string;
  link?: string;
}

const Tenants: React.FC = () => {
  /* --------------------------------- Redux ------------------------------ */

  const dispatch = useAppDispatch();
  const { programList, programLoading, programError } = useAppSelector((state: RootState) => state.program);
  const { principalError, principalLoading, principalList } = useAppSelector((state: RootState) => state.principal);
  const { tenantLoading, tenantError, tenantList, tenantVpaMappings, preSelectedPrincipal, preSelectedProgram } = useAppSelector((state: RootState) => state.tenant);

  /* --------------------------------- States ------------------------------ */

  const [principalId, setPrincipalId] = useState<number | string | null>(preSelectedPrincipal);
  const [prevPrincipalId, setPrevPrincipalId] = useState<number | string | null>(principalId);
  const [programId, setProgramId] = useState<number | string | null>(preSelectedProgram);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTenant, setSelectedTenant] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");


  /* --------------------------------- Hooks ------------------------------ */

  useEffect(() => {
    dispatch(fetchPrincipalList());
    dispatch(fetchTenantList({ principalId, programId }));
  }, []);

  useEffect(() => {
    if (principalId !== prevPrincipalId) {
      const fetchData = async () => {
        await dispatch(fetchProgramList({ principalId }));
        await dispatch(fetchTenantList({ principalId, page: 1, searchTerm }));
      };
      fetchData();
    }

    setPrevPrincipalId(principalId);
  }, [dispatch, principalId]);

  useEffect(() => {
    dispatch(fetchTenantList({ principalId, programId, page: currentPage, searchTerm }));
  }, [dispatch, programId, currentPage]);

  /* ------------------------------ Event Handlers ---------------------------- */

  const handleEditClick = (tenant: Tenant) => {
    setEditingTenant(tenant);
    setShowEditModal(true);
  };

  const refreshData = () => {
    dispatch(fetchTenantList({ principalId, programId, page: 1, searchTerm }));
  }

  const handleSearch = () => {
    dispatch(fetchTenantList({ principalId, programId, page: 1, searchTerm }));
  };

  const downloadProgramVpas = async() => {

    if (!programId) return;

    const response = await dispatch(fetchProgramVpaMappings({ programId: Number(programId) }));
    const programResponse = await dispatch(fetchProgramById(Number(programId)));

    if (response.type.endsWith('/fulfilled') && programResponse.type.endsWith('/fulfilled')) {
      const programName = programList?.find(p => p.programId === programId)?.programName || 'program';
      const merchantId = programResponse.payload?.merchantId;

      const data = response.payload.flatMap((mapping: any) =>
        mapping.vpa.map((vpa: any) => ({
          MERCHANT_ID: merchantId,
          VPA: vpa.vpa,
          "STATUS (Enable/Disable)": vpa.status === 'ACTIVE' ? 'ENABLE' : 'DISABLE'
        }))
      ) || [];

      // Create workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Sheet1');

      // Define columns
      worksheet.columns = [
        { header: 'MERCHANT_ID', key: 'MERCHANT_ID', width: 20 },
        { header: 'VPA', key: 'VPA', width: 30 },
        { header: 'STATUS (Enable/Disable)', key: 'STATUS (Enable/Disable)', width: 20 }
      ];

      // Add data rows
      worksheet.addRows(data);

      // Style header row
      const headerRow = worksheet.getRow(1);

      // Apply styles to each header cell
      headerRow.eachCell((cell) => {
        cell.font = {
          name: 'Calibri',
          size: 10,
          bold: true,
        };

      });

      // Generate and download file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${programName}_vpas.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };



  /* ------------------------------ UI Handlers ---------------------------- */


  const getContent = () => {
    let header = (
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Outlets ({tenantList?.total_count || 0})</h1>
      </div>
    );
    let header_sidebar = (
      <div className="flex items-center gap-3">
        <div className="relative">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-4 pr-10 py-2 border border-cardbox rounded-md text-sm font-medium text-gray-500 bg-white  focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition-colors"
          />
          <button
            onClick={handleSearch}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
          >
            <Search className="w-4 h-4" />
          </button>
        </div>

        <div className="relative">
          <select
            value={principalId || ""}
            onChange={(e) => {
              setProgramId(null);
              setCurrentPage(1);
              setPrincipalId(Number(e.target.value));
            }}
            className="appearance-none pl-4 pr-10 py-2 border border-cardbox rounded-md text-sm font-medium text-gray-500 bg-white  focus:outline-none    transition-colors"
          >
            <option value="">All Principals</option>
            {principalList?.map((principal) => (
              <option key={principal.principalId} value={principal.principalId}>
                {principal.name}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
            </svg>
          </div>
        </div>

        <div className="relative">
          <select
            value={programId || ""}
            onChange={(e) => {
              setCurrentPage(1);
              setProgramId(Number(e.target.value));
            }}
            disabled={!principalId}
            className="appearance-none pl-4 pr-10 py-2 border border-cardbox rounded-md text-sm font-medium text-gray-500 bg-white hover:border-gray-300 focus:outline-none transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">All Programs</option>
            {programList?.map((program) => (
              <option key={program.programId} value={program.programId}>
                {program.programName}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
            </svg>
          </div>
        </div>

        <button
          onClick={() => setShowUploadModal(true)}
          className="px-4 py-2 rounded-md text-sm font-medium text-white bg-brand hover:bg-brand/80 focus:outline-none focus:border-0 focus:ring-1 focus:ring-brand transition-colors"
        >
          Add Outlets
        </button>

        {programId && tenantList && tenantList.total_count > 0 && (
          <button
            onClick={downloadProgramVpas}
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-brand hover:bg-brand/80 focus:outline-none focus:border-0 focus:ring-1 focus:ring-brand transition-colors"
          >
            Download VPAs
          </button>
        )}
      </div>
    );

    if (principalLoading || programLoading || tenantLoading) {
      return (
        <div className="bg-white min-h-screen">
          <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
            <div className="flex items-center h-10">
              {header}
            </div>
            <div className="flex items-center h-10">
              {header_sidebar}
            </div>
          </div>
          <div className="flex justify-center items-center h-[calc(100vh-20rem)]">
            <div className="flex items-center gap-2">
              <Loader2 className="w-10 h-10 animate-spin text-brand/80" />
              <span className="text-sm text-gray-500">Loading...</span>
            </div>
          </div>
        </div>
      );
    }

    if (principalError || programError || tenantError) {
      return (
        <div className="bg-white">
          <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
            <div className="flex items-center h-10">
              {header}
            </div>
            <div className="flex items-center h-10">
              {header_sidebar}
            </div>
          </div>
          <div className="flex justify-center items-center min-h-[200px]">
            <div className="flex items-center gap-2 text-red-500">
              <AlertCircle className="w-5 h-5" />
              <span>{tenantError || principalError || programError || "Something went wrong!"}</span>
            </div>
          </div>
        </div>
      );
    }

    if (selectedTenant && tenantVpaMappings) {
      return (
        <div className="bg-white min-h-screen">
          <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-8 py-8">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSelectedTenant(null)}
                className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">VPA Listing</h1>
            </div>

          </div>
          <div className="max-w-7xl mx-auto">
            <div className="section_container solid">
              <VpaListing
                tenant={selectedTenant}
                tenantVpaMappings={tenantVpaMappings}
                tenantLoading={tenantLoading}
                tenantError={tenantError}
                onBack={() => setSelectedTenant(null)}
              />
            </div>
          </div>
        </div>
      );
    }

    if (tenantList) {
      const tenantListProps = {
        tenantList,
        handleEditClick,
        setSelectedTenant,
        showUploadModal,
        setShowUploadModal,
        programList,
        principalList,
        programId,
        setProgramId,
        principalId,
        setPrincipalId,
        currentPage,
        setCurrentPage,
        searchTerm,
        setSearchTerm
      };

      const editTenantProps = {
        programId,
        principalId,
        setEditingTenant,
        editingTenant,
        showEditModal,
        setShowEditModal,
      };

      const bulkTenantUploadProps = {
        programId,
        principalId,
        showUploadModal,
        setShowUploadModal,
        refreshData
      };

      return (
        <div className="bg-white min-h-screen">
          <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-8 py-8">
            <div className="flex items-center h-10">
              {header}
            </div>
            <div className="flex items-center h-10">
              {header_sidebar}
            </div>
          </div>
          <div className="max-w-7xl mx-auto">
            <div className="section_container solid">
              <TenantList {...tenantListProps} />
              <EditTenant {...editTenantProps} />
              <BulkTenantUpload {...bulkTenantUploadProps} />
            </div>
          </div>
        </div>
      );
    }
  };

  return getContent();
};

export default Tenants;
